import { BASE_URL, request } from "../../../api/api";
import type {
  PaymentProviderRequest,
  PaymentProviderResponse,
  WompiApiResponse,
  WompiErrorResponse
} from "../types/types";
import { WebhookUrlService } from "../../webhookUrls";
import axios from "axios";

export class PaymentProviderService {
  /**
   * Get available payment providers
   */
  public static async getProviders(): Promise<string[]> {
    try {
      const response = await request<string[]>(`${BASE_URL}/api/PaymentProvider/providers`, {
        method: "GET"
      });
      return response;
    } catch (error: unknown) {
      console.error("Error fetching payment providers:", error);
      throw error;
    }
  }

  /**
   * Create payment link using the unified PaymentProvider API
   * This will automatically route to the appropriate provider (N1co, Wompi, etc.)
   */
  public static async createPaymentLink(
    data: PaymentProviderRequest
  ): Promise<PaymentProviderResponse> {
    try {
      const response = await request<WompiApiResponse>(
        `${BASE_URL}/api/PaymentProvider/create-payment-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }
      );

      // Extract the payment URL with proper priority
      const paymentUrl = response.urlEnlace || response.paymentUrl || response.url;
      
      if (!paymentUrl) {
        console.error("No payment URL found in response:", response);
        return {
          success: false,
          error: "No se recibió URL de pago válida del proveedor"
        };
      }

      return {
        success: true,
        paymentUrl: paymentUrl,
      };
    } catch (error: unknown) {
      console.error("Error creating payment link via PaymentProvider:", error);

      let errorMessage = "Error creating payment link";

      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as WompiErrorResponse;
        errorMessage = errorData?.message || errorData?.error || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Create payment link with specific provider (backward compatibility)
   */
  public static async createPaymentLinkWithProvider(
    provider: string,
    amortizationId: number,
    options?: {
      emailsNotificacion?: string;
      urlRedirect?: string;
      urlWebhook?: string;
    }
  ): Promise<PaymentProviderResponse> {
    const data: PaymentProviderRequest = {
      provider,
      amortizationId,
      ...options
    };

    return this.createPaymentLink(data);
  }

  /**
   * Generate webhook URL for the application
   */
  public static generateWebhookUrl(provider: string): string {
    // Use the centralized webhook URL service
    return WebhookUrlService.getWebhookUrl(provider);
  }

  /**
   * Generate redirect URL for successful payments
   */
  public static generateSuccessRedirectUrl(amortizationId: number, provider?: string): string {
    return WebhookUrlService.getSuccessRedirectUrl(amortizationId, provider);
  }

  /**
   * Generate redirect URL for failed payments
   */
  public static generateErrorRedirectUrl(amortizationId: number, provider?: string, error?: string): string {
    return WebhookUrlService.getErrorRedirectUrl(amortizationId, provider, error);
  }
}
